import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import {
  WORKING_DAY_ITEMS,
  MAX_INTERVALS_PER_DAY,
  defaultWorkingHours,
  parseTimeString,
  withDefaultWorkingHours,
} from "../../utils/workingHours";
import { useWorkingHours } from "../../context/WorkingHoursContext";

const TIME_STEP = 30;

const normalizeDay = (day, fallback) => {
  if (!day) return { ...fallback };
  const intervals =
    Array.isArray(day.intervals) && day.intervals.length
      ? day.intervals.slice(0, MAX_INTERVALS_PER_DAY).map((interval, index) => ({
          start: interval?.start || fallback.intervals[index]?.start || "09:00",
          end: interval?.end || fallback.intervals[index]?.end || "20:30",
        }))
      : fallback.intervals;
  return {
    enabled: typeof day.enabled === "boolean" ? day.enabled : fallback.enabled,
    intervals,
  };
};

export default function WorkingHoursModal({ open, onClose }) {
  const { workingHours, saveWorkingHours, saving } = useWorkingHours();
  const [formState, setFormState] = useState(defaultWorkingHours());
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const merged = withDefaultWorkingHours(workingHours);
    const normalized = {};
    WORKING_DAY_ITEMS.forEach(({ key }) => {
      normalized[key] = normalizeDay(merged[key], defaultWorkingHours()[key]);
    });
    setFormState(normalized);
    setError("");
  }, [open, workingHours]);

  const hoursSummary = useMemo(
    () =>
      WORKING_DAY_ITEMS.map(({ key, label }) => ({
        key,
        label,
        ...(formState[key] || {}),
      })),
    [formState]
  );

  const handleToggle = (key) => {
    setFormState((prev) => {
      const fallback = defaultWorkingHours()[key];
      const current = prev[key] || fallback;
      const next = {
        ...current,
        enabled: !current.enabled,
        intervals: current.intervals?.map((interval) => ({ ...interval })) || [],
      };
      if (next.enabled && next.intervals.length === 0) {
        next.intervals = fallback.intervals.map((interval) => ({ ...interval }));
      }
      if (!next.enabled) {
        next.intervals = [];
      }
      return {
        ...prev,
        [key]: next,
      };
    });
  };

  const handleIntervalChange = (dayKey, index, field, value) => {
    setFormState((prev) => {
      const day = prev[dayKey] || { enabled: false, intervals: [] };
      const sourceIntervals = Array.isArray(day.intervals) && day.intervals.length
        ? day.intervals
        : defaultWorkingHours()[dayKey].intervals;
      const intervals = sourceIntervals.map((interval, idx) =>
        idx === index ? { ...interval, [field]: value } : interval
      );
      return {
        ...prev,
        [dayKey]: {
          ...day,
          intervals,
        },
      };
    });
  };

  const handleAddInterval = (dayKey) => {
    setFormState((prev) => {
      const day = prev[dayKey] || { enabled: false, intervals: [] };
      const currentIntervals = Array.isArray(day.intervals) ? day.intervals : [];
      if (currentIntervals.length >= MAX_INTERVALS_PER_DAY) return prev;
      const fallback =
        defaultWorkingHours()[dayKey].intervals[0] || { start: "09:00", end: "20:30" };
      const last = currentIntervals[currentIntervals.length - 1] || fallback;
      const nextInterval = {
        start: last.end || "13:00",
        end: "20:30",
      };
      return {
        ...prev,
        [dayKey]: {
          ...day,
          enabled: true,
          intervals: [...currentIntervals, nextInterval].slice(0, MAX_INTERVALS_PER_DAY),
        },
      };
    });
  };

  const handleRemoveInterval = (dayKey, index) => {
    setFormState((prev) => {
      const day = prev[dayKey];
      if (!day) return prev;
      const currentIntervals = Array.isArray(day.intervals) ? day.intervals : [];
      const nextIntervals = currentIntervals.filter((_, idx) => idx !== index);
      return {
        ...prev,
        [dayKey]: {
          ...day,
          intervals: nextIntervals,
        },
      };
    });
  };

  const copyWeekdayToAll = () => {
    const defaults = defaultWorkingHours();
    const template =
      formState.monday ||
      formState.tuesday ||
      normalizeDay(defaults.monday, defaults.monday);

    const updated = {};
    WORKING_DAY_ITEMS.forEach(({ key }) => {
      updated[key] = {
        enabled: template.enabled,
        intervals: template.intervals.map((interval) => ({ ...interval })),
      };
    });
    setFormState(updated);
    toast.info("Programul a fost copiat pentru întreaga săptămână.");
  };

  const validatePayload = () => {
    const payload = {};
    for (const { key, label } of WORKING_DAY_ITEMS) {
      const cfg = formState[key] || defaultWorkingHours()[key];
      if (!cfg.enabled) {
        payload[key] = { enabled: false, intervals: [] };
        continue;
      }

      const intervals = cfg.intervals || [];
      if (!intervals.length) {
        throw new Error(`Adaugă cel puțin un interval pentru ${label}.`);
      }

      const sanitizedIntervals = intervals.map((interval) => {
        const startValue = parseTimeString(interval.start);
        const endValue = parseTimeString(interval.end);
        if (!Number.isFinite(startValue) || !Number.isFinite(endValue)) {
          throw new Error(`Ore invalide pentru ${label}.`);
        }
        if (endValue <= startValue) {
          throw new Error(`Ora de sfârșit trebuie să fie după ora de început pentru ${label}.`);
        }
        return { start: interval.start, end: interval.end };
      });

      payload[key] = {
        enabled: true,
        intervals: sanitizedIntervals,
      };
    }
    return payload;
  };

  const handleSave = async () => {
    try {
      setError("");
      const payload = validatePayload();
      await saveWorkingHours(payload);
      toast.success("Program salvat cu succes.");
      onClose?.();
    } catch (err) {
      const message = err.message || "Nu am putut salva programul.";
      setError(message);
      toast.error(message);
    }
  };

  if (!open) return null;

  return (
    <Modal title="Setează programul de lucru" onClose={onClose} size="xl" contentClassName="max-w-5xl">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Configurează până la două intervale de lucru pentru fiecare zi. Sloturile din calendar se vor adapta automat la aceste ore.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={copyWeekdayToAll}>
            Copiază programul în toată săptămâna
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setFormState(defaultWorkingHours())}>
            Resetează la implicit
          </Button>
        </div>

        <div className="divide-y rounded-xl border border-gray-200 overflow-hidden">
          {hoursSummary.map(({ key, label, enabled = true, intervals = [] }) => (
            <div key={key} className="space-y-3 px-4 py-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="font-semibold text-gray-800">{label}</p>
                  <p className="text-xs text-gray-500">
                    {enabled && intervals.length
                      ? intervals.map((interval) => `${interval.start} – ${interval.end}`).join(" | ")
                      : "Zi liberă"}
                  </p>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-400"
                    checked={enabled}
                    onChange={() => handleToggle(key)}
                  />
                  Activă
                </label>
              </div>

              {enabled && (
                <div className="space-y-2">
                  {intervals.map((interval, idx) => (
                    <div key={idx} className="flex flex-wrap items-center gap-3">
                      <label className="flex items-center gap-2 text-sm text-gray-600">
                        Start
                        <input
                          type="time"
                          step={TIME_STEP * 60}
                          value={interval.start}
                          onChange={(event) =>
                            handleIntervalChange(key, idx, "start", event.target.value)
                          }
                          className="rounded-lg border border-gray-300 px-3 py-1 text-sm"
                        />
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-600">
                        Sfârșit
                        <input
                          type="time"
                          step={TIME_STEP * 60}
                          value={interval.end}
                          onChange={(event) =>
                            handleIntervalChange(key, idx, "end", event.target.value)
                          }
                          className="rounded-lg border border-gray-300 px-3 py-1 text-sm"
                        />
                      </label>
                      {intervals.length > 1 && (
                        <Button
                          variant="secondary"
                          size="sm"
                          className="!px-2 !py-1"
                          onClick={() => handleRemoveInterval(key, idx)}
                        >
                          Elimină
                        </Button>
                      )}
                    </div>
                  ))}
                  {intervals.length < MAX_INTERVALS_PER_DAY && (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="mt-1"
                      onClick={() => handleAddInterval(key)}
                    >
                      Adaugă interval
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Se salvează..." : "Salvează programul"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
